import { pick } from 'highcharts'

import { GraphDatapoint } from '../types/graphSeriesConfig'

interface SplinePoint {
  plotX: number
  plotY: number
  isNull: boolean
  rightContX?: number
  rightContY?: number
}

interface Point {
  x: number
  y: number
}

interface BezierPointCollection {
  p0: Point
  p1: Point
  p2: Point
  p3: Point
}

export const generatePrecisePlotPoints = (points: Array<GraphDatapoint>): Array<GraphDatapoint> => {
  const bezierPoints: Array<BezierPointCollection> = []
  const splinePoints = points.map((x) => {
    return {
      plotX: x.msec,
      plotY: x.dataValue || 0,
      isNull: x.dataValue === null,
    }
  })

  // we cannot calculate bezier curve from a series with less than 2 points
  if (points.length < 2) {
    return []
  }

  // get array of all points + control points
  for (let i = 1; i < splinePoints.length; i++) {
    const bezierPoint = getPointSpline(splinePoints, splinePoints[i], i)
    bezierPoints.push({ p0: bezierPoint.p0, p1: bezierPoint.p1, p2: bezierPoint.p2, p3: bezierPoint.p3 })
  }
  // add first point
  const finalDatapoints: Array<GraphDatapoint> = [{ msec: bezierPoints[0].p0.x, dataValue: bezierPoints[0].p0.y }]

  // calculate number of points between two x-values
  bezierPoints.forEach((x) => {
    const firstX = x.p0.x
    const lastX = x.p3.x

    for (let i = firstX + 1000; i <= lastX; i += 1000) {
      const tValues = solveCubicBezier(x.p0.x, x.p1.x, x.p2.x, x.p3.x, i)
      const bezierResult = bezier(tValues[0], x.p0, x.p1, x.p2, x.p3)
      finalDatapoints.push({
        msec: Math.round(bezierResult.x),
        dataValue: bezierResult.y,
      })
    }
  })

  return finalDatapoints
}

// highcharts function to calculate splines (simplified). Taken from https://github.com/highcharts/highcharts/blob/master/ts/Series/Spline/SplineSeries.ts
const getPointSpline = (points: Array<SplinePoint>, point: SplinePoint, i: number): BezierPointCollection => {
  // 1 means control points midway between points, 2 means 1/3 from the point, 3 is 1/4 etc
  let smoothing = 1.5,
    denom = smoothing + 1,
    plotX = point.plotX || 0,
    plotY = point.plotY || 0,
    lastPoint = points[i - 1],
    nextPoint = points[i + 1],
    leftContX: number | undefined,
    leftContY: number | undefined,
    rightContX: number,
    rightContY: number,
    ret: BezierPointCollection

  const doCurve = (otherPoint: SplinePoint): boolean => {
    return otherPoint && !otherPoint.isNull
  }

  // Find control points
  if (doCurve(lastPoint) && doCurve(nextPoint)) {
    let lastX = lastPoint.plotX || 0,
      lastY = lastPoint.plotY || 0,
      nextX = nextPoint.plotX || 0,
      nextY = nextPoint.plotY || 0,
      correction = 0

    leftContX = (smoothing * plotX + lastX) / denom
    leftContY = (smoothing * plotY + lastY) / denom
    rightContX = (smoothing * plotX + nextX) / denom
    rightContY = (smoothing * plotY + nextY) / denom

    // Have the two control points make a straight line through main point
    if (rightContX !== leftContX) {
      // #5016, division by zero
      correction = ((rightContY - leftContY) * (rightContX - plotX)) / (rightContX - leftContX) + plotY - rightContY
    }

    leftContY += correction
    rightContY += correction

    // to prevent false extremes, check that control points are between neighbouring points' y values
    if (leftContY > lastY && leftContY > plotY) {
      leftContY = Math.max(lastY, plotY)
      // mirror of left control point
      rightContY = 2 * plotY - leftContY
    } else if (leftContY < lastY && leftContY < plotY) {
      leftContY = Math.min(lastY, plotY)
      rightContY = 2 * plotY - leftContY
    }

    if (rightContY > nextY && rightContY > plotY) {
      rightContY = Math.max(nextY, plotY)
      leftContY = 2 * plotY - rightContY
    } else if (rightContY < nextY && rightContY < plotY) {
      rightContY = Math.min(nextY, plotY)
      leftContY = 2 * plotY - rightContY
    }

    // record for drawing in next point
    point.rightContX = rightContX
    point.rightContY = rightContY
  }

  ret = {
    p0: { x: pick(lastPoint.plotX, 0), y: pick(lastPoint.plotY, 0) },
    p1: { x: pick(lastPoint.rightContX, lastPoint.plotX, 0), y: pick(lastPoint.rightContY, lastPoint.plotY, 0) },
    p2: { x: pick(leftContX, plotX, 0), y: pick(leftContY, plotY, 0) },
    p3: { x: plotX, y: plotY },
  }

  // reset for updating series later
  lastPoint.rightContX = lastPoint.rightContY = undefined
  return ret
}

// get bezier points based on t. Taken from https://stackoverflow.com/questions/16227300/how-to-draw-bezier-curves-with-native-javascript-code-without-ctx-beziercurveto
const bezier = (t: number, p0: Point, p1: Point, p2: Point, p3: Point): Point => {
  const cX = 3 * (p1.x - p0.x),
    bX = 3 * (p2.x - p1.x) - cX,
    aX = p3.x - p0.x - cX - bX

  const cY = 3 * (p1.y - p0.y),
    bY = 3 * (p2.y - p1.y) - cY,
    aY = p3.y - p0.y - cY - bY

  const x = aX * Math.pow(t, 3) + bX * Math.pow(t, 2) + cX * t + p0.x
  const y = aY * Math.pow(t, 3) + bY * Math.pow(t, 2) + cY * t + p0.y

  return { x: x, y: y }
}

// get t-values given an x (and control points) Cubic equations always have at least one real answer.
// Taken from https://stackoverflow.com/questions/7348009/y-coordinate-for-a-given-x-cubic-bezier
const solveCubicBezier = (p0: number, p1: number, p2: number, p3: number, x: number): Array<number> => {
  p0 -= x
  p1 -= x
  p2 -= x
  p3 -= x

  const a = p3 - 3 * p2 + 3 * p1 - p0
  const b = 3 * p2 - 6 * p1 + 3 * p0
  const c = 3 * p1 - 3 * p0
  const d = p0

  const roots = solveCubic(a, b, c, d)

  const roundToDecimal = (num: number, dec: number) => {
    return Math.round(num * Math.pow(10, dec)) / Math.pow(10, dec)
  }

  return roots.map((root) => roundToDecimal(root, 15)).filter((root) => root >= 0 && root <= 1)
}

// solve cubic equation; return only real root(s). Taken from https://stackoverflow.com/questions/27176423/function-to-solve-cubic-equation-analytically
const solveCubic = (a: number, b: number, c: number, d: number): Array<number> => {
  const cuberoot = (x: number): number => {
    const y = Math.pow(Math.abs(x), 1 / 3)
    return x < 0 ? -y : y
  }

  if (Math.abs(a) < 1e-8) {
    // Quadratic case, ax^2+bx+c=0
    a = b
    b = c
    c = d
    if (Math.abs(a) < 1e-8) {
      // Linear case, ax+b=0
      a = b
      b = c
      if (Math.abs(a) < 1e-8) {
        // Degenerate case
        return []
      }
      return [-b / a]
    }

    const D = b * b - 4 * a * c
    if (Math.abs(D) < 1e-8) {
      return [-b / (2 * a)]
    } else if (D > 0) {
      return [(-b + Math.sqrt(D)) / (2 * a), (-b - Math.sqrt(D)) / (2 * a)]
    }
    return []
  }

  // Convert to depressed cubic t^3+pt+q = 0 (subst x = t - b/3a)
  const p = (3 * a * c - b * b) / (3 * a * a)
  const q = (2 * b * b * b - 9 * a * b * c + 27 * a * a * d) / (27 * a * a * a)
  let roots

  if (Math.abs(p) < 1e-8) {
    // p = 0 -> t^3 = -q -> t = -q^1/3
    roots = [cuberoot(-q)]
  } else if (Math.abs(q) < 1e-8) {
    // q = 0 -> t^3 + pt = 0 -> t(t^2+p)=0
    roots = [0].concat(p < 0 ? [Math.sqrt(-p), -Math.sqrt(-p)] : [])
  } else {
    const D = (q * q) / 4 + (p * p * p) / 27
    if (Math.abs(D) < 1e-8) {
      // D = 0 -> two roots
      roots = [(-1.5 * q) / p, (3 * q) / p]
    } else if (D > 0) {
      // Only one real root
      const u = cuberoot(-q / 2 - Math.sqrt(D))
      roots = [u - p / (3 * u)]
    } else {
      // D < 0, three roots, but needs to use complex numbers/trigonometric solution
      const u = 2 * Math.sqrt(-p / 3)
      const t = Math.acos((3 * q) / p / u) / 3 // D < 0 implies p < 0 and acos argument in [-1..1]
      const k = (2 * Math.PI) / 3
      roots = [u * Math.cos(t), u * Math.cos(t - k), u * Math.cos(t - 2 * k)]
    }
  }

  // Convert back from depressed cubic
  for (let i = 0; i < roots.length; i++) {
    roots[i] -= b / (3 * a)
  }

  return roots
}
